// Excel cell helpers for the workbook exports (SheetJS / xlsx).
//
// Why this exists: writing "1/1/2026" as a string produces a *text* cell.
// It looks right, but Excel won't sort it chronologically, won't accept it in
// date functions, and the format dropdown reports "Text". Every date we export
// must be a real date cell — a numeric serial carrying a date number format —
// so it both reads as M/D/YYYY and behaves as a date.
//
// The cell objects returned here are accepted verbatim by SheetJS's
// `aoa_to_sheet`, `sheet_add_aoa`, and `json_to_sheet`, so a single helper
// covers every export path.
import { parseDateValue, formatDate, formatDateTime } from './formatters';

// M/D/YYYY with no leading zeros — matches formatDate, so the cell reads the
// same in the workbook as it does on screen.
export const EXCEL_DATE_FMT = 'm/d/yyyy';

// Excel's day-zero. 1899-12-30 (not 12-31) absorbs Excel's deliberate
// 1900-leap-year bug for every date from 1900-03-01 on, which is the range
// SheetJS itself treats as valid. Computed in UTC so a DST boundary can never
// shift a calendar day.
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Calendar date → Excel serial number. Takes a Date carrying the local
// year/month/day (what parseDateValue returns).
export function excelDateSerial(date) {
  return (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - EXCEL_EPOCH_UTC) / MS_PER_DAY;
}

// Any date-ish value → a real Excel date cell. Returns '' for empty or
// unparseable values so blank cells stay blank rather than rendering an error
// or "NaN/NaN/NaN".
export function excelDateCell(value) {
  const date = parseDateValue(value);
  if (!date) return '';
  // `w` is the cached display text; Excel recomputes it from `z`, but keeping
  // it makes the cell readable to anything that inspects the value (e.g. the
  // column-width pass on the raw dumps).
  return { t: 'n', v: excelDateSerial(date), z: EXCEL_DATE_FMT, w: formatDate(date) };
}

// Same idea one rung finer: a real Excel date-TIME cell. Only for values where
// the time of day is the point — an export stamp, where two files produced the
// same day otherwise look identical and you cannot tell which is which.
// Everything that represents a calendar day (posting dates, statement dates,
// dates of birth) stays on excelDateCell.
export const EXCEL_DATETIME_FMT = 'm/d/yyyy h:mm AM/PM';

// Excel serials carry time as a fraction of a day, so the integer part is the
// same value excelDateSerial gives and the fraction is local time of day.
export function excelDateTimeSerial(date) {
  const secondsIntoDay = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
  return excelDateSerial(date) + secondsIntoDay / 86400;
}

export function excelDateTimeCell(value) {
  // NOT parseDateValue for a Date input — it flattens to local midnight, which
  // would silently throw away the very thing this helper exists to keep.
  const date = value instanceof Date ? value : parseDateValue(value);
  if (!date || isNaN(date.getTime())) return '';
  return {
    t: 'n',
    v: excelDateTimeSerial(date),
    z: EXCEL_DATETIME_FMT,
    w: formatDateTime(date),
  };
}

// Strict ISO 8601 — "2026-01-01" or "2026-01-01T12:30:00Z". Deliberately
// narrow: the generic export runs this over every string value in the feed,
// and a loose matcher would turn account numbers, reference codes, or free
// text into dates.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

export function isIsoDateString(value) {
  return typeof value === 'string' && ISO_DATE_RE.test(value);
}

// Row objects → the same rows with every strict-ISO string replaced by a date
// cell. For the generic `json_to_sheet` export, where the columns aren't known
// ahead of time. Non-date values pass through untouched.
//
// Only top-level values are examined — a feed of nested records keeps its
// dates out of reach, so callers with nested data must flatten first.
export function coerceIsoDateCells(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
    let changed = false;
    const out = {};
    Object.keys(row).forEach(key => {
      const value = row[key];
      if (isIsoDateString(value)) {
        out[key] = excelDateCell(value);
        changed = true;
      } else {
        out[key] = value;
      }
    });
    return changed ? out : row;
  });
}
